import { useState } from 'react';
import { Pencil, Trash, Info, Plus } from 'lucide-react';
import {
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { WorkshopV2 } from '../../../interfaces/workshop.interface';
import { getBookingModeLabel } from '../../../utils/workshopMigration';
import {
  formatClosingDate,
  isBookingClosed,
} from '../../../utils/workshopClosing';
import EditWorkshopPopup from './EditWorkshopPopup';
import ConfirmationModal from '../../../components/ConfirmationModal';
import ParticipantsPopup from './ParticipantsPopup';
import AddReservationPopup from './AddReservationPopUp';


interface Props {
  workshop: WorkshopV2;
  onUpdated: () => void;
}

const WorkshopCard = ({ workshop, onUpdated }: Props) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAddReservation, setShowAddReservation] = useState(false);

  const date = workshop.date.toDate().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Fermeture automatique : l'atelier reste listé en admin (on peut encore
  // ajouter une résa à la main), mais il n'est plus visible côté client.
  const closed = isBookingClosed(workshop);
  const closesAt = workshop.bookingClosesAt
    ? formatClosingDate(workshop.bookingClosesAt.toDate())
    : null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'workshops_v2', workshop.id));
      onUpdated();
    } catch (err) {
      console.error('❌ Erreur suppression atelier :', err);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 bg-white p-4 rounded shadow-sm border border-[#623413]/10">
        <div className="flex items-center gap-4">
          <img
            src={workshop.imageUrl || '/defaultimages/defaultcake.png'}
            alt={workshop.title}
            className="h-16 w-16 object-cover rounded border border-[#623413]/20"
          />
          <div className="flex flex-col">
            <p className="text-base font-semibold text-[#623413]">
              {workshop.title}
            </p>
            <p className="text-sm text-gray-600">{date}</p>
            <p className="text-sm text-gray-500">
              Durée : {workshop.duration} min — Mode :{' '}
              {getBookingModeLabel(workshop.bookingMode)}
            </p>
            <p className="text-sm text-gray-500">
              {workshop.priceSolo > 0 && `Solo : ${workshop.priceSolo.toFixed(2)} €`}
              {workshop.priceSolo > 0 && workshop.priceDuo > 0 && ' — '}
              {workshop.priceDuo > 0 && `Duo : ${workshop.priceDuo.toFixed(2)} €`}
              {' — '}Max : {workshop.maxParticipants} — Restantes : {workshop.remainingPlaces}
            </p>
            {closesAt && (
              <p className="text-sm text-gray-500">
                {closed
                  ? `Réservations closes depuis le ${closesAt}`
                  : `Réservations ouvertes jusqu'au ${closesAt}`}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Bouton ajouter une réservation manuelle */}
          <button
            onClick={() => setShowAddReservation(true)}
            className="inline-flex items-center justify-center rounded-full p-2 border border-[#623413]/20 text-[#623413] hover:bg-[#623413]/5 transition"
            title="Ajouter un participant"
          >
            <Plus size={20} />
          </button>

          <button
            onClick={() => setShowParticipants(true)}
            className="text-gray-500 hover:text-[#623413] transition-colors"
            title="Participants"
          >
            <Info size={20} />
          </button>

          <button
            onClick={() => setShowEdit(true)}
            className="text-gray-500 hover:text-[#623413] transition-colors"
            title="Modifier"
          >
            <Pencil size={20} />
          </button>

          <button
            onClick={() => setShowConfirm(true)}
            className="text-gray-500 hover:text-[#BB0F33] transition-colors"
            disabled={deleting}
            title="Supprimer"
          >
            <Trash size={20} />
          </button>

          <span
            className={`ml-4 text-sm font-medium ${
              !workshop.active
                ? 'text-red-500'
                : closed
                  ? 'text-amber-600'
                  : 'text-green-600'
            }`}
            title={
              closed && closesAt ? `Fermeture automatique le ${closesAt}` : undefined
            }
          >
            {!workshop.active ? 'Inactif' : closed ? 'Résa fermée' : 'Actif'}
          </span>
        </div>
      </div>

      {showEdit && (
        <EditWorkshopPopup
          workshop={workshop}
          onClose={() => setShowEdit(false)}
          onUpdated={onUpdated}
        />
      )}

      {showParticipants && (
        <ParticipantsPopup
          workshopId={workshop.id}
          onClose={() => setShowParticipants(false)}
        />
      )}

      {showAddReservation && (
        <AddReservationPopup
          workshopId={workshop.id}
          onClose={() => setShowAddReservation(false)}
          onCreated={onUpdated} // pour rafraîchir si besoin
        />
      )}

      {showConfirm && (
        <ConfirmationModal
          message="Voulez-vous vraiment supprimer cet atelier ?"
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default WorkshopCard;
